import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import styled from "styled-components";
import { FiZap } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";

import MainContainer from "../components/MainContainer";
import ProductCard from "../components/utils/ProductCard";
import SkeletonProductCard from "../components/utils/SkeletonProductCard";
import CustomSelect from "../components/utils/CustomSelect";
import {
  selectCategories,
  selectProducts,
} from "../store/slices/products/slice";
import {
  getProducts,
  getProductsByCategory,
} from "../store/slices/products/asyncThunks";

const SORTS = [
  { value: "default", label: "Sortiraj: Istaknuto" },
  { value: "pasc", label: "Cena: niža → viša" },
  { value: "pdesc", label: "Cena: viša → niža" },
  { value: "name", label: "Naziv: A → Z" },
];

const effectivePrice = (p) =>
  p?.salePrice !== undefined && p?.salePrice !== null && p?.salePrice !== ""
    ? Number(p.salePrice)
    : Number(p?.price);

const ShopPage = () => {
  const products = useSelector(selectProducts);
  const categories = useSelector(selectCategories);
  const location = useLocation();
  const category = location.state?.kategorija;
  const dispatch = useDispatch();

  const [activeCategory, setActiveCategory] = useState("Sve");
  const [sort, setSort] = useState("default");
  const [productsCategoryLoading, setProductsCategoryLoading] = useState(false);

  useEffect(() => {
    dispatch(getProducts())
      .unwrap()
      .then(() => {})
      .catch((err) => console.log(err.message)); // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (category) {
      setActiveCategory(category);
    }
  }, [category]);

  useEffect(() => {
    if (activeCategory !== "Sve" && activeCategory !== "Akcija") {
      setProductsCategoryLoading(true);
      dispatch(getProductsByCategory({ id: activeCategory, page: 1 }))
        .unwrap()
        .then(() => {
          setProductsCategoryLoading(false);
        })
        .catch((err) => console.log(err.message));
    } // eslint-disable-next-line
  }, [activeCategory]);

  const selectedProducts = useMemo(() => {
    let list;

    if (activeCategory === "Sve") {
      list = products.data;
    } else if (activeCategory === "Akcija") {
      list = products.data.filter(
        (p) =>
          p?.salePrice !== undefined &&
          p?.salePrice !== null &&
          p?.salePrice !== "",
      );
    } else {
      list = products.productsByCategory?.[activeCategory]?.data?.data || [];
    }

    const sorted = [...list];
    if (sort === "pasc")
      sorted.sort((a, b) => effectivePrice(a) - effectivePrice(b));
    if (sort === "pdesc")
      sorted.sort((a, b) => effectivePrice(b) - effectivePrice(a));
    if (sort === "name")
      sorted.sort((a, b) => (a?.name || "").localeCompare(b?.name || "", "sr"));

    return sorted;
  }, [products, activeCategory, sort]);

  const loading = products.loading || productsCategoryLoading;

  const pickCategory = (name) => {
    setActiveCategory(name);
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 300);
  };

  return (
    <MainContainer setActiveCategory={setActiveCategory} offCenter hideLogo>
      <Head>
        <SLabel>Obsessed — kompletna kolekcija</SLabel>
        <Title>
          Prodavnica <sup>/{loading ? "…" : selectedProducts.length}</sup>
        </Title>
      </Head>

      <FilterBar>
        <FilterRow>
          <Chips>
            <Chip
              $active={activeCategory === "Sve"}
              onClick={() => pickCategory("Sve")}
            >
              Sve
            </Chip>
            {categories.data.map((cat) => (
              <Chip
                key={cat.name}
                $active={activeCategory === cat.name}
                onClick={() => pickCategory(cat.name)}
              >
                {cat.name}
              </Chip>
            ))}
            <Chip
              $sale
              $active={activeCategory === "Akcija"}
              onClick={() => pickCategory("Akcija")}
            >
              <FiZap size={13} />
              Akcija
            </Chip>
          </Chips>

          <SortWrap>
            <CustomSelect
              value={sort}
              onChange={setSort}
              options={SORTS.map((s) => ({ value: s.value, label: s.label }))}
            />
          </SortWrap>
        </FilterRow>
      </FilterBar>

      {loading ? (
        <ProductGrid>
          {Array.from({ length: 8 }).map((_, i) => (
            <SkeletonProductCard key={i} />
          ))}
        </ProductGrid>
      ) : selectedProducts.length === 0 ? (
        <Empty>
          <span>無</span>
          Trenutno nema proizvoda u ovoj kategoriji.
        </Empty>
      ) : (
        <ProductGrid>
          {selectedProducts.map((product, index) => (
            <ProductCard key={product?.id ?? index} product={product} shop />
          ))}
        </ProductGrid>
      )}
    </MainContainer>
  );
};

export default ShopPage;

const Head = styled.div`
  width: 100%;
  max-width: 132rem;
  margin: 0 auto;
  padding: 6rem clamp(1.6rem, 4vw, 4.8rem) 3rem;
`;

const SLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 1.2rem;
  font-size: 1.1rem;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: var(--red);
  font-family: "Poppins-Medium";
  margin-bottom: 1.4rem;

  &::before {
    content: "";
    width: 3.4rem;
    height: 1px;
    background: var(--red);
  }
`;

const Title = styled.h1`
  font-family: "Anton", sans-serif;
  font-weight: 400;
  text-transform: uppercase;
  font-size: clamp(3.8rem, 5.6vw, 6.4rem);
  line-height: 1;
  letter-spacing: 0.01em;
  color: var(--text);

  sup {
    font-size: 0.3em;
    color: var(--red);
    letter-spacing: 0.1em;
  }
`;

const FilterBar = styled.div`
  position: sticky;
  top: 0;
  z-index: 800;
  width: 100%;
  background: rgba(10, 10, 12, 0.88);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border-top: 1px solid var(--line);
  border-bottom: 1px solid var(--line);
  padding: 1.4rem 0;
  margin-bottom: 3.6rem;
`;

const FilterRow = styled.div`
  max-width: 132rem;
  margin: 0 auto;
  padding: 0 clamp(1.6rem, 4vw, 4.8rem);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.6rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const Chips = styled.div`
  display: flex;
  gap: 0.8rem;
  flex-wrap: nowrap;
  overflow-x: auto;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (min-width: 769px) {
    flex-wrap: wrap;
    overflow: visible;
  }
`;

const Chip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.2rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  font-family: "Poppins-Medium";
  padding: 0.8rem 1.6rem;
  border-radius: 10rem;
  white-space: nowrap;
  cursor: pointer;
  border: 1px solid
    ${(props) =>
      props.$active
        ? "var(--red)"
        : props.$sale
          ? "var(--red)"
          : "var(--line2)"};
  background: ${(props) => (props.$active ? "var(--red)" : "transparent")};
  color: ${(props) =>
    props.$active ? "var(--bg0)" : props.$sale ? "var(--red)" : "var(--muted)"};
  font-weight: ${(props) => (props.$active ? 700 : 400)};
  transition: all 0.3s var(--ease);

  &:hover {
    color: ${(props) => (props.$active ? "var(--bg0)" : "var(--text)")};
    border-color: ${(props) => (props.$active ? "var(--red)" : "var(--text)")};
  }
`;

const SortWrap = styled.div`
  width: 22rem;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1.8rem;
  width: 100%;
  max-width: 132rem;
  margin: 0 auto;
  padding: 0 clamp(1.6rem, 4vw, 4.8rem);
  min-height: 52.15rem;
  align-content: start;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const Empty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.4rem;
  min-height: 52.15rem;
  justify-content: center;
  width: 100%;
  font-family: "Poppins-Medium";
  font-size: 1.4rem;
  color: var(--muted);

  span {
    font-family: var(--font-kanji);
    font-size: 8rem;
    color: var(--muted2);
    line-height: 1;
  }
`;
